/**
 * Persistence for the user's theme override — the half of the theming contract
 * that was missing. `apps/marketing`'s no-flash bootstrap already READS
 * `localStorage['theme']` before first paint, so this module writes exactly
 * that key in exactly that shape:
 *
 *   'light' | 'dark'  → an explicit override, wins over `prefers-color-scheme`
 *   key absent        → `auto`, the stylesheet's media query decides
 *
 * Storing `'auto'` as a literal is deliberately NOT done: the absence of the
 * key is the auto state, so marketing's bootstrap and this provider agree
 * without either having to know the other's vocabulary.
 *
 * Pure over an injected {@link ThemeStorage}, so it is testable without a DOM
 * and safe under SSR (every call is a no-op when there is no `localStorage`).
 */
import type { ThemeOverride } from './resolve-theme.ts';

/** The slice of the Web Storage API this module needs. */
export interface ThemeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The storage key. Shared with `apps/marketing`'s pre-paint bootstrap. */
export const THEME_STORAGE_KEY = 'theme';

/** `localStorage` when it exists and is reachable, else `undefined`. */
export function defaultThemeStorage(): ThemeStorage | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return localStorage;
  } catch {
    // Blocked by privacy settings — treat as "no storage", never throw.
    return undefined;
  }
}

/**
 * Read the persisted override. Anything that is not `light`/`dark` — a missing
 * key, a stale `'auto'`, garbage written by an older build — resolves to
 * `auto` rather than throwing or stamping a nonsense theme.
 */
export function readThemeOverride(
  storage: ThemeStorage | undefined,
  key: string = THEME_STORAGE_KEY,
): ThemeOverride {
  if (!storage) return 'auto';
  try {
    const raw = storage.getItem(key);
    return raw === 'light' || raw === 'dark' ? raw : 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * Persist the override, removing the key for `auto`. Storage can throw (private
 * mode, blocked cookies, quota); the theme still applies for this page view, it
 * just will not survive a reload — never let that break the UI.
 */
export function writeThemeOverride(
  storage: ThemeStorage | undefined,
  override: ThemeOverride,
  key: string = THEME_STORAGE_KEY,
): void {
  if (!storage) return;
  try {
    if (override === 'auto') storage.removeItem(key);
    else storage.setItem(key, override);
  } catch {
    // storage unavailable — the applied theme still holds for this page view
  }
}
